import { useEffect, useMemo, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { every } from 'lodash';
import {
  fetchFields,
  fetchFieldsGlobalOrder,
  globalOrderSelector,
  saveStagedGlobalFieldOrder,
} from 'slices/field.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { TField } from '@timeedit/types';

export const useGlobalFieldOrder = () => {
  const dispatch = useAppDispatch();
  const globalFieldOrder = useAppSelector(globalOrderSelector);
  const [stagedGlobalFieldOrder, setStagedGlobalFieldOrder] =
    useState<TField[]>(globalFieldOrder);

  useEffect(() => {
    dispatch(fetchFieldsGlobalOrder());
    dispatch(fetchFields());
  }, [dispatch]);

  useEffect(() => {
    setStagedGlobalFieldOrder((prev) =>
      prev.length === 0 ? globalFieldOrder : prev
    );
  }, [globalFieldOrder]);

  const hasLocalChanges = useMemo<boolean>(() => {
    return !every(
      stagedGlobalFieldOrder,
      (field, index) => field.id === globalFieldOrder[index]?.id
    );
  }, [globalFieldOrder, stagedGlobalFieldOrder]);

  const changeGlobalOrder = (moveField: TField, targetField: TField) => {
    const moveIndex = stagedGlobalFieldOrder.findIndex(
      (field) => field.id === moveField.id
    );
    const targetIndex = stagedGlobalFieldOrder.findIndex(
      (field) => field.id === targetField.id
    );
    if (moveIndex === -1 || targetIndex === -1) return;
    setStagedGlobalFieldOrder(
      arrayMove(stagedGlobalFieldOrder, moveIndex, targetIndex)
    );
  };

  const reset = () => {
    setStagedGlobalFieldOrder(globalFieldOrder);
  };

  const saveGlobalOrder = () => {
    dispatch(saveStagedGlobalFieldOrder(stagedGlobalFieldOrder));
  };

  return {
    globalFieldOrder,
    changeGlobalOrder,
    hasLocalChanges,
    saveGlobalOrder,
    stagedGlobalFieldOrder,
    reset,
  };
};
